import { Suspense, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { getMinutesFromDate, MILLISECONDS_IN_MINUTE, MINUTES_IN_DAY } from "@/lib/format-date"
import { formatInServerTimeZone } from "@/lib/initial-time-zone"
import { joinStrings } from "@/lib/joinString"
import { useShiftQuery$data } from "@/modules/productivity-cockpit/services/__generated__/useShiftQuery.graphql"
import { useShiftQuery } from "@/modules/productivity-cockpit/services/useShiftQuery"
import { SHIFT_BACKGROUND_MAP } from "@/modules/productivity-cockpit/types/ShiftBackgroundShift"
import { ArrElement } from "@/types/ArrElement"

interface ShiftItemProps extends React.HTMLAttributes<HTMLElement> {
  shift: ArrElement<useShiftQuery$data["todayShiftOverview"]>
}

const Shifts = () => {
  const { todayShiftOverview } = useShiftQuery()

  return useMemo(() => {
    if (todayShiftOverview) return todayShiftOverview.map((shift) => <ShiftItem key={shift.shiftStart} shift={shift} />)
    return null
  }, [todayShiftOverview])
}

const ShiftItem = ({ shift }: ShiftItemProps) => {
  const { t } = useTranslation()
  const { shiftStart, shiftEnd, shiftInformation, shiftType } = shift
  const start = formatInServerTimeZone(shiftStart, "HH:mm")
  const end = formatInServerTimeZone(shiftEnd, "HH:mm")
  const startDate = formatInServerTimeZone(shiftStart)
  const startMinutes = getMinutesFromDate(new Date(startDate))

  const diffMinutes = Math.floor(
    (new Date(shiftEnd).getTime() - new Date(shiftStart).getTime()) / MILLISECONDS_IN_MINUTE,
  )

  const Divider = () => {
    const start = 60 - (startMinutes % 60)

    return Array.from({ length: Math.ceil((diffMinutes - start) / 60) }, (_, i) => {
      const style = {
        left: `calc(${start + i * 60} * 100% / ${diffMinutes})`,
      }
      return <div key={i} className="bg-border absolute h-4.5 w-px opacity-75" style={style}></div>
    })
  }

  return (
    <TooltipCustom key={start}>
      <TooltipTrigger asChild>
        <div
          key={start}
          className={`absolute h-4.5 cursor-pointer rounded-full ${SHIFT_BACKGROUND_MAP[shiftInformation]}`}
          style={{
            width: `calc(${diffMinutes} * 100% / ${MINUTES_IN_DAY})`,
            left: `calc(${startMinutes} * 100% / ${MINUTES_IN_DAY})`,
          }}
          data-testid={`shift-bar-${start}-${end}`}>
          <Divider />
          <small
            className="text-on-surface-subdued absolute top-full left-0 mt-1 text-[9.6px]"
            data-testid={`start-time-at-${start}`}>
            {start}
          </small>
          <small
            className="text-on-surface-subdued absolute top-full right-0 mt-1 text-[9.6px]"
            data-testid={`end-time-at-${end}`}>
            {end}
          </small>
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent align="start" side="bottom">
          {shiftInformation === "NON_OPERATIVE_SHIFT" ? t("scheduling.productivityCockpit.nonOperative") : shiftType}
          <div className="mt-1">{joinStrings([start, end], " - ")}</div>
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

export const CapacityTodayShift = () => {
  const { t } = useTranslation()
  const today = formatInServerTimeZone(new Date().toISOString())
  const minutes = getMinutesFromDate(new Date(today))
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    const updateCurrentTime = () => setCurrentTime(new Date())
    const interval = setInterval(updateCurrentTime, 60 * 1000)
    updateCurrentTime()

    return () => clearInterval(interval)
  }, [])

  const now = formatInServerTimeZone(currentTime.toISOString(), "HH:mm")

  return (
    <Suspense>
      <div className="grid w-1/2 gap-2 p-4 pb-8" data-testid="today-shift-section">
        <div className="text-on-surface pb-8 text-base leading-6 font-medium" data-testid="today-shift-title">
          {t("scheduling.productivityCockpit.todayShift")}
        </div>
        <div className="relative">
          <div
            className="item-center bg-border relative flex h-6 w-135.75 items-center justify-between gap-1 rounded-full shadow-inner [&>div:first-of-type>small:first-of-type]:block [&>div:last-of-type>small:last-of-type]:block [&>div>small]:hidden"
            data-testid="today-shift-bar">
            <Shifts />
          </div>
          <div
            style={{ left: `calc(${minutes} * 100% / ${MINUTES_IN_DAY})` }}
            className="bg-surface-inverted text-on-surface-inverted absolute bottom-full z-0 mb-2 grid -translate-x-1/2 rounded-md border-none p-1 text-center font-semibold">
            <small className="leading-none opacity-50" data-testid="now-label">
              {t("common.now")}
            </small>
            <small className="leading-tight" data-testid="current-time">
              {now}
            </small>
            <div className="bg-surface-inverted absolute top-full left-1/2 z-[-1] h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45"></div>
            <div className="border-on-surface-subdued absolute top-full left-1/2 mt-2 h-6 -translate-x-1/2 border-l-2 border-dotted"></div>
          </div>
        </div>
        <div className="mt-8 flex gap-8">
          <div className="flex items-center gap-1">
            <Badge className="bg-primary h-3 w-3 shrink-0 p-0" />
            {t("scheduling.productivityCockpit.operative")}
          </div>
          <div className="flex items-center gap-1">
            <Badge className="bg-critical h-3 w-3 shrink-0 p-0" />
            {t("scheduling.productivityCockpit.medicalSupervisor")}
          </div>
          <div className="flex items-center gap-1">
            <Badge className="bg-success h-3 w-3 shrink-0 p-0" />
            {t("scheduling.productivityCockpit.nonOperative")}
          </div>
          <div className="flex items-center gap-1">
            <Badge className="bg-border h-3 w-3 shrink-0 p-0 shadow-inner" />
            {t("scheduling.productivityCockpit.off")}
          </div>
        </div>
      </div>
    </Suspense>
  )
}
